import { isAfter, isBefore, compareAsc, compareDesc, differenceInSeconds } from "date-fns";
import { calculateDurationMeta } from "../../epg-editor/utils/trim-program-duration";
import { v4 as uuid } from "uuid";
import dateIsBetween from "../../../common/date-is-between";

export function duplicateProgramToDay(program, day) {
  const programCopy = { ...program, drop_id: uuid(), template_day_item_id: null };
  let newItems = [...day.items];

  /// BREAKS ///
  let newBreaks = [...day.templateBreaks]
    .map((tBreak) => {
      if (tBreak.type === "channel" && dateIsBetween(tBreak.end, programCopy.since, programCopy.till, "()")) {
        // trim program if conflicts with a channel break
        programCopy.till = tBreak.end;
        const durationMeta = calculateDurationMeta(
          programCopy,
          differenceInSeconds(programCopy.till, programCopy.since),
          program.ad_breaks,
        );
        programCopy.duration_seconds = durationMeta.duration_seconds;
        programCopy.total_duration_seconds = durationMeta.total_duration_seconds;
        programCopy.__gstvMeta = {
          ...programCopy.__gstvMeta,
          ...durationMeta,
        };
      }
      return { break_time: tBreak.end, type: tBreak.type };
    })
    .filter((tBreak) => !dateIsBetween(tBreak.break_time, programCopy.since, programCopy.till, "()")); // remove conflicting plan breaks

  // if not starting on a break we need to add one
  if (!startsOnBreak(day.templateBreaks, programCopy.since)) {
    newBreaks = [...newBreaks, { break_time: programCopy.since, type: "plan" }].sort((a, b) => {
      return compareAsc(a.break_time, b.break_time);
    });
  }

  /// PROGRAMS ///
  // handle program conflicts
  let overlapItems = getOverlappingItems(newItems, program);
  newItems = newItems
    .map((item, index) => {
      const overrideItem = overlapItems.find((oItem) => oItem.index === index);
      if (overrideItem?.overwriteType === "delete") {
        return null;
      }

      return overrideItem ?? item;
    })
    .filter((item) => item !== null);

  let insertIndex = getLastItemBeforeIndex(newItems, programCopy.since) + 1; // default to after previous program, if not exists will set to 0 which is fine
  newItems.splice(insertIndex, 0, programCopy);

  return {
    items: newItems,
    breaks: newBreaks,
  };
}

export function getLastItemBeforeIndex(items, programStart) {
  let programIndex = items.findIndex((s) => s.since.toString() === programStart.toString());

  if (programIndex === -1) {
    // if no program with same start time, try getting last
    programIndex = items.findLastIndex((item) => isBefore(item.since, programStart));
  }

  return programIndex;
}

export function startsOnBreak(breaks, programStart) {
  let breakIndex = breaks.findIndex((tBreak) => tBreak.start.toString() === programStart.toString());

  return breakIndex !== -1;
}

export function getOverlappingItems(list, program) {
  const start = program.since;
  const end = program.till;

  const overlapPrograms = list
    .map((program, index) => {
      if (!(isBefore(program.since, end) && isBefore(start, program.till))) {
        return null;
      }

      let nextProgram = { ...program, index, overwriteType: "keep" };
      // determine type of overlap
      if (compareAsc(program.since, start) > -1 && compareDesc(program.till, end) > -1) {
        // delete if wrapped by new program
        nextProgram.overwriteType = "delete";
      } else if (isBefore(program.since, start)) {
        // trim bottom
        nextProgram.till = start;
        nextProgram.overwriteType = "trim-top";
      } else if (isAfter(program.till, end)) {
        // trim top
        nextProgram.since = end;
        nextProgram.overwriteType = "trim-bottom";
      }

      // nuke programs with less than 5mins remaining after trim
      if (differenceInSeconds(nextProgram.till, nextProgram.since) <= 5 * 60) {
        nextProgram.overwriteType = "delete";
      }

      // post trim recalculations
      if (["trim-top", "trim-bottom"].includes(nextProgram.overwriteType)) {
        const durationMeta = calculateDurationMeta(
          nextProgram,
          differenceInSeconds(nextProgram.till, nextProgram.since),
          program.ad_breaks,
        );
        nextProgram.duration_seconds = durationMeta.duration_seconds;
        nextProgram.total_duration_seconds = durationMeta.total_duration_seconds;
        nextProgram.__gstvMeta = {
          ...nextProgram.__gstvMeta,
          ...durationMeta,
        };
      }

      return nextProgram;
    })
    .filter((program) => program !== null);

  return overlapPrograms;
}
